<script setup>
import { WIcon } from '@alice-health/wonderland-vue'
import debounce from 'lodash/debounce'
import { computed, onMounted, ref } from 'vue'

import { HSelect } from '@/components/shared/atoms'
import { DEMAND_TYPES } from '@/models/demands'

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Demandas relacionadas ao membro'
  },
  value: {
    type: Object,
    default: undefined
  },
  disabled: {
    type: Boolean
  },
  enableHealthCondition: {
    type: Boolean
  },
  fetchFunction: {
    type: Function,
    default: () => []
  },
  searchFunction: {
    type: Function,
    default: () => []
  }
})

const emit = defineEmits(['input'])

const search = ref(null)
const loading = ref(false)
const abortController = ref(null)
const demandsFiltered = ref([])
const healthConditionsFiltered = ref([])
const demands = ref([])
const updatedTime = ref(new Date().getTime())

const UPDATE_FUNCTION_TIMER = 10000

const selectedValue = computed(() => {
  return props.value
    ? {
        ...props.value,
        displayValue: getDisplayValue(props.value) || props.value.description
      }
    : null
})

const demandOptions = computed(() => {
  const options = []
  const activeDemands = (demandsFiltered.value || []).map(demand => ({
    ...demand,
    displayValue: getDisplayValue(demand)
  }))

  const conditions = healthConditionsFiltered.value.map(demand => ({
    ...demand,
    displayValue: demand.description
  }))

  if (activeDemands.length) {
    options.push({
      displayValue: 'Demandas do membro',
      divider: true
    })
    options.push(...activeDemands)
  }
  if (conditions.length) {
    options.push({ displayValue: 'Novas demandas', divider: true })
    options.push(...conditions)
  }

  return options
})

onMounted(() => {
  getDemands()
})

const resetCases = () => {
  demandsFiltered.value = demands.value
}

const filterDemands = filter => {
  const filterLower = filter?.toLowerCase()
  return demands.value.filter(demand => {
    const description = demand.description?.description?.toLowerCase()
    const value = demand.description?.value?.toLowerCase()
    if (description?.includes(filterLower) || value?.includes(filterLower)) {
      return true
    }
  })
}

const handleDemandSelection = demand => {
  search.value = null
  if (demand?.divider) return
  emit('input', demand)
  healthConditionsFiltered.value = []
  resetCases()
}

const handleInput = value => {
  healthConditionsFiltered.value = []
  if (value.length >= 3) {
    demandsFiltered.value = filterDemands(value)
    if (!demandsFiltered.value.length && props.enableHealthCondition) {
      searchConditions(value)
    }
  } else {
    resetCases()
  }
}

const searchConditions = debounce(async query => {
  let results = []

  abortController.value?.abort()
  abortController.value = new AbortController()
  loading.value = true
  results = await searchHealthCondition(query, abortController.value.signal)
  loading.value = false
  healthConditionsFiltered.value = results
}, 200)

const searchHealthCondition = (query, signal) => {
  return props.searchFunction({
    query,
    checkRole: true,
    options: {
      signal
    }
  })
}

const getDemands = async () => {
  loading.value = true
  const result = await props.fetchFunction({})
  if (result) {
    demands.value = result
    resetCases()
    updatedTime.value = new Date().getTime()
  }
  loading.value = false
}

const shouldUpdateDemandList = () => {
  return updatedTime.value + UPDATE_FUNCTION_TIMER < new Date().getTime()
}

const updateDemands = open => {
  if (open && shouldUpdateDemandList()) {
    getDemands()
  }
}

const getDisplayValue = demand => {
  if (demand.description.type === DEMAND_TYPES.GOAL) {
    return demand.description?.description || demand.description.value
  }
  return demand.description?.description
}
</script>

<template>
  <HSelect
    id="demand-dropdown"
    :options="demandOptions"
    option-key="caseId"
    option-label="displayValue"
    :value="selectedValue"
    :disabled="disabled"
    :placeholder="placeholder"
    @update:value="handleDemandSelection"
    @input="handleInput"
    @focusout="resetCases"
    @toggle="updateDemands"
  >
    <template v-if="enableHealthCondition" #iconLeft>
      <WIcon icon="icSearch" />
    </template>

    <template #options="{ option }">
      <span v-if="option.divider" class="demand-select__divider">
        {{ option.displayValue }}
      </span>
      <span v-else>
        {{ option.displayValue }}
      </span>
    </template>

    <template #iconRight>
      <WIcon v-if="loading" icon="icLoad" class="channel-select__loading" />
    </template>
  </HSelect>
</template>

<style lang="scss" scoped>
.demand-select {
  &__divider {
    letter-spacing: var(--gl-letter-spacing-rule-02);
    font-weight: var(--gl-font-weight-regular);
    font-size: var(--gl-font-size-xxxs);
    line-height: var(--gl-line-height-xxxs);
    color: var(--gl-color-shades-gray-60);
  }
}
</style>
